/**
 * @fileoverview `useAccessRequestSubmit(endpoint)` — the "Request access" submission every login
 * surface sends.
 * @module @puffin-core/login-shell/hooks/useAccessRequestSubmit
 *
 * UNREACHABLE-MODULE-BURNDOWN-001 L16. Until this existed, `<ContactManagerModal>` SIMULATED
 * success when its host passed no `onSubmit`, and not one of the fleet's tenant `<LoginShell>`
 * mounts passed one — so on every tenant sign-in page "Contact your store manager" waited 500 ms,
 * said the request had been sent, and sent nothing. Only the marketing login posted for real.
 *
 * The endpoint DEFAULTS rather than being required, the Q-UMB-20 shape ("safe default + stated
 * opt-out"): every tenant API mounts `createAccessRequestRouter` at `/v1/auth/access-request`
 * (census: all nine) and every web app reaches its API through the `/api` prefix, so the default
 * is right for every tenant page by construction and a page cannot forget it. A surface whose API
 * lives elsewhere passes its own `accessRequestEndpoint`.
 */

import { useCallback } from 'react';
import { usePageLanguage } from './usePageLanguage.js';
import { createPublicJsonPoster } from '../utils/publicJsonPoster.js';

/** The canonical access-request route, as a web app reaches it through its `/api` proxy. */
export const DEFAULT_ACCESS_REQUEST_ENDPOINT = '/api/v1/auth/access-request';

const postJson = createPublicJsonPoster('ContactManagerModal');

/**
 * @param {string} [endpoint=DEFAULT_ACCESS_REQUEST_ENDPOINT] - the access-request route.
 * @returns {(request: {name: string, email: string, message: string, subdomain: string,
 *   website: string}) => Promise<any>} posts the request; rejects with the
 *   AUTH-ERROR-VOCABULARY-001 error (`status`, `errorCode`, …) so the modal can say what failed.
 *   `website` is the honeypot's value — the router's bot check reads that field by name. The
 *   request also carries the `language` the page is showing (EMAIL-LOCALE-ZONE-001 owner ruling #6,
 *   "The sign-in page's language"): the requester has no account, so their confirmation email is
 *   written in it. With no i18n instance there is none, and the server writes in the company's.
 */
export function useAccessRequestSubmit(endpoint = DEFAULT_ACCESS_REQUEST_ENDPOINT) {
  const language = usePageLanguage();
  return useCallback(
    ({ name, email, message, subdomain, website }) =>
      postJson(endpoint, { name, email, message, subdomain, website, language }),
    [endpoint, language],
  );
}

export default useAccessRequestSubmit;
